import React, { useRef, useState } from 'react';
import { UserSwitchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Flex, Form, InputRef, Modal } from 'antd';
import { AntdForm } from '@/components/AntdForm';
import { useImpersonateUserMutation } from '@/features/auth/auth.api.ts';
import { handleApiError } from '@/utils/api.tsx';
import { AntdFormRules } from '@/components/AntdForm/rules.ts';
import { notification } from '@/utils/antdAppFeedback.ts';

type FormValues = {
  username: string;
};

export const ImpersonateUserModal: React.FC = () => {
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState(false);
  const [needValidate, setNeedValidate] = useState(true);
  const [impersonateUser, { isLoading }] = useImpersonateUserMutation();
  const [form] = Form.useForm();

  const showModal = () => {
    setOpen(true);
  };

  const onFinish = async (data: FormValues) => {
    const result = await impersonateUser({
      envName: import.meta.env.VITE_IR_PORTAL_ENV,
      username: data.username,
    });
    if ('data' in result) {
      notification.success({ message: `Impersonate as ${data.username} successfully` });
      form.resetFields();
      setOpen(false);
      location.reload();
    } else {
      handleApiError(result.error, 'Impersonation failed');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setNeedValidate(true);
    setOpen(false);
  };

  return (
    <>
      <Button icon={<UserSwitchOutlined />} onClick={showModal}>
        <span className="hidden sm:inline">Impersonate User</span>
      </Button>
      <Modal
        open={open}
        title="Impersonate User"
        destroyOnHidden
        onCancel={handleCancel}
        afterOpenChange={(isOpen) => {
          if (!isOpen) return;

          window.setTimeout(() => {
            inputRef.current?.focus();
          }, 300);
        }}
        footer={null}
      >
        <Form layout="vertical" scrollToFirstError form={form} onFinish={onFinish}>
          <AntdForm.Input
            allowClear
            ref={inputRef}
            fieldProps={{
              name: 'username',
              rules: [AntdFormRules.required],
              validateDebounce: 1000,
            }}
          />
          <Checkbox checked={needValidate} onChange={(e) => setNeedValidate(e.target.checked)}>
            Validate username
          </Checkbox>
          <Flex gap="small" justify="flex-end">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Save
            </Button>
          </Flex>
        </Form>
      </Modal>
    </>
  );
};
